import { useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import type { KnowledgeEdge, KnowledgeEntity } from '../api/knowledgeApi';

function formatValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function PropRow({ name, value }: { name: string; value: unknown }) {
  const formatted = formatValue(value);
  const multiline = formatted.includes('\n') || formatted.length > 80;
  return (
    <div className="space-y-1 border-b border-border/60 py-2 last:border-b-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {name}
      </div>
      {multiline ? (
        <ScrollArea className="max-h-48 rounded bg-muted/40">
          <pre className="px-2 py-1.5 font-mono text-xs whitespace-pre-wrap text-foreground">
            {formatted}
          </pre>
        </ScrollArea>
      ) : (
        <div className="text-sm text-foreground">{formatted}</div>
      )}
    </div>
  );
}

export function KnowledgeEntityDetails({
  entity,
  edges,
  className,
  emptyLabel,
}: {
  entity: KnowledgeEntity | null;
  edges: KnowledgeEdge[];
  className?: string;
  emptyLabel?: string;
}) {
  const { t } = useI18n();
  if (!entity) {
    return (
      <div className={cn('flex min-h-0 items-start p-4', className)}>
        <p className="text-sm text-muted-foreground">{emptyLabel ?? t.knowledge.selectNode}</p>
      </div>
    );
  }

  const props = entity.props ?? {};
  const propEntries = Object.entries(props).sort(([a], [b]) => a.localeCompare(b));
  const outgoing = edges.filter(
    (edge) => edge.from_label === entity.label && edge.from_id === entity.id,
  );
  const incoming = edges.filter(
    (edge) => edge.to_label === entity.label && edge.to_id === entity.id,
  );

  return (
    <div className={cn('min-h-0 p-4', className)}>
      <div className="space-y-1">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {entity.label}
          {entity.domain ? ` · ${entity.domain}` : ''}
          {entity.locale ? ` · ${entity.locale}` : ''}
        </div>
        <h2 className="text-base font-semibold leading-snug">
          {String(props.name ?? props.title ?? props.key ?? entity.id)}
        </h2>
        <div className="font-mono text-xs text-muted-foreground">{entity.id}</div>
      </div>

      <section className="mt-5">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t.common.properties}
        </h3>
        {propEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.knowledge.noProperties}</p>
        ) : (
          <div>
            {propEntries.map(([name, value]) => (
              <PropRow key={name} name={name} value={value} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-5">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t.knowledge.outgoing(outgoing.length)}
        </h3>
        {outgoing.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.common.none}</p>
        ) : (
          <ul className="space-y-1.5">
            {outgoing.map((edge, index) => (
              <li
                key={`out-${edge.type}-${edge.to_label}-${edge.to_id}-${index}`}
                className="rounded border bg-muted/30 px-2 py-1.5 text-xs"
              >
                <span className="font-medium text-foreground">-[{edge.type}]→</span>{' '}
                <span className="text-muted-foreground">
                  {edge.to_label}/{edge.to_id}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-5">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t.knowledge.incoming(incoming.length)}
        </h3>
        {incoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.common.none}</p>
        ) : (
          <ul className="space-y-1.5">
            {incoming.map((edge, index) => (
              <li
                key={`in-${edge.type}-${edge.from_label}-${edge.from_id}-${index}`}
                className="rounded border bg-muted/30 px-2 py-1.5 text-xs"
              >
                <span className="text-muted-foreground">
                  {edge.from_label}/{edge.from_id}
                </span>{' '}
                <span className="font-medium text-foreground">-[{edge.type}]→</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
